import React, { useState, useRef, useEffect, useMemo } from 'react';
import useStore from '../store/useStore';
import typesByLanguage from '../data/typesByLanguage';

export default function TypeAutocomplete({ value, onChange, nodeId, autoOpen }) {
  const [open, setOpen] = useState(!!autoOpen);
  const [filter, setFilter] = useState(value || '');
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const language = useStore((s) => s.language);
  const getEntityNames = useStore((s) => s.getEntityNames);

  const suggestions = useMemo(() => {
    const langTypes = typesByLanguage[language] || [];
    const entityNames = getEntityNames().filter((n) => {
      const currentNode = useStore.getState().nodes.find((nd) => nd.id === nodeId);
      return n !== currentNode?.data?.name;
    });
    const all = [...langTypes, ...entityNames];
    if (!filter) return all;
    const lower = filter.toLowerCase();
    return all.filter((t) => t.toLowerCase().includes(lower));
  }, [language, filter, getEntityNames, nodeId]);

  useEffect(() => {
    setFilter(value || '');
  }, [value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        if (open) {
          // Save current filter as value when clicking outside
          if (filter !== value) {
            onChange(filter);
          }
          setOpen(false);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open, filter, value, onChange]);

  const handleSelect = (type) => {
    setFilter(type);
    onChange(type);
    setOpen(false);
    inputRef.current?.blur();
  };

  return (
    <div className="type-autocomplete" ref={containerRef}>
      <textarea
        ref={inputRef}
        className="type-input nodrag"
        value={filter}
        onChange={(e) => {
          setFilter(e.target.value);
          setOpen(true);
          e.target.style.height = 'auto';
          e.target.style.height = e.target.scrollHeight + 'px';
        }}
        onFocus={(e) => {
          setOpen(true);
          e.target.style.height = 'auto';
          e.target.style.height = e.target.scrollHeight + 'px';
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            onChange(filter);
            setOpen(false);
            e.target.blur();
          }
          if (e.key === 'Escape') {
            setFilter(value);
            setOpen(false);
            e.target.blur();
          }
        }}
        placeholder="Type..."
        rows={1}
      />
      {open && suggestions.length > 0 && (
        <div className="type-dropdown nodrag nopan nowheel">
          {suggestions.map((type, i) => (
            <div
              key={`${type}-${i}`}
              className={`type-option ${type === value ? 'active' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                handleSelect(type);
              }}
            >
              {type}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
